// Olaf Ragnarök cast VFX — blood-red berserker burst with axe streaks.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const DURATION = 600;
const STREAK_COUNT = 8;

export class OlafRagnarokVFX implements Animation {
  private elapsed = 0;

  constructor(
    private center: Point,
    private radius: number,
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, DURATION);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const t = this.elapsed / DURATION;
    const expandT = Easing.easeOutBack(Math.min(t * 1.6, 1));
    const fade = t < 0.55 ? 1 : 1 - (t - 0.55) / 0.45;

    ctx.save();

    // Outer blood-red ring
    ctx.globalAlpha = fade * 0.85;
    ctx.strokeStyle = "#cc2200";
    ctx.lineWidth = 5;
    ctx.shadowColor = "#ff3300";
    ctx.shadowBlur = 28;
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, this.radius * expandT, 0, Math.PI * 2);
    ctx.stroke();

    // Mid ring (brighter orange-red)
    ctx.globalAlpha = fade * 0.6;
    ctx.strokeStyle = "#ff5500";
    ctx.lineWidth = 2.5;
    ctx.shadowBlur = 16;
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, this.radius * expandT * 0.7, 0, Math.PI * 2);
    ctx.stroke();

    // Inner fill pulse
    ctx.globalAlpha = fade * 0.18;
    ctx.fillStyle = "#ff2200";
    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, this.radius * expandT * 0.8, 0, Math.PI * 2);
    ctx.fill();

    // Axe-streak lines radiating outward
    ctx.globalAlpha = fade * 0.75;
    ctx.strokeStyle = "#ff8844";
    ctx.lineWidth = 2;
    ctx.shadowColor = "#ff4400";
    ctx.shadowBlur = 12;
    for (let i = 0; i < STREAK_COUNT; i++) {
      const angle = (i / STREAK_COUNT) * Math.PI * 2 + t * 1.2;
      const inner = this.radius * expandT * 0.55;
      const outer = this.radius * expandT * 1.15;
      ctx.beginPath();
      ctx.moveTo(this.center.x + Math.cos(angle) * inner, this.center.y + Math.sin(angle) * inner);
      ctx.lineTo(this.center.x + Math.cos(angle) * outer, this.center.y + Math.sin(angle) * outer);
      ctx.stroke();
    }

    // Small sparks at ring edge
    ctx.shadowBlur = 8;
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2 - t * 2.5;
      const r = this.radius * expandT;
      const sx = this.center.x + Math.cos(angle) * r;
      const sy = this.center.y + Math.sin(angle) * r;
      ctx.globalAlpha = fade * 0.9;
      ctx.fillStyle = "#ffaa44";
      ctx.beginPath();
      ctx.arc(sx, sy, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= DURATION;
  }
}
